import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { effectiveGrade } from '../utils/fourPoint';
import Logo from '../components/Logo';
import api from '../services/api';
import { printDocument, printBtnStyle } from '../utils/print';
import { DEFECT_TYPES, POINT_RULES, GRADE_COLOR } from '../utils/fourPoint';
import { KpiCard, ListFooter } from '../ui';
import GreigeQC from '../components/GreigeQC';
import { askReason } from '../utils/reportError';

// ---- 7 Quality tabs ----------------------------------------------------------
const TABS = [
  { key: 'panel_check', label: '🧵 Panel Check' },
  { key: 'tls', label: '🏷 TLS Audit' },
  { key: 'inline', label: '🔍 Inline Inspection' },
  { key: 'final', label: '✅ Final Inspection' },
  { key: 'aql', label: '📐 AQL Calculator' },
  { key: 'defects', label: '🐞 Defect Tracker' },
  // NOT 'Defects' — that name is taken above by the read-only analytics view, and two
  // tabs a letter apart is a trap. This is a RECORD of material faults found after the
  // GRN passed, and what became of them.
  { key: 'material_faults', label: '⚠ Material Faults' },
  { key: 'rm_inward', label: '📦 Raw Material Inward' },
  { key: 'store_qc', label: '🔬 Store Receipt QC' },
  { key: 'greige_qc', label: '🧵 Greige QC' },
];

// Per-inspection-type config — drives the heading, helper text and which
// columns/fields make sense. All four inspection tabs share one backed table
// (quality_inspections) keyed by inspection_type.
const INSP = {
  panel_check: {
    title: '🧵 Panel Check', blurb: 'Cut-panel inspection before stitching — shade, print, measurement and fabric-defect check per panel bundle.',
    opLabel: 'Panel / Part', opPlaceholder: 'e.g. Front body', showLine: true, lineLabel: 'Bundle No',
    qtyLabel: 'Panels Checked', defLabel: 'Panels Rejected', logHeading: 'Log Panel Check',
  },
  tls: {
    title: '🏷 TLS Audit', blurb: 'Trims, Labels & Sundries audit — verify care/brand labels, hangtags, polybags and trim accessories against the approved card.',
    opLabel: 'Trim / Label Item', opPlaceholder: 'e.g. Care label', showLine: false,
    qtyLabel: 'Qty Audited', defLabel: 'Discrepancies', logHeading: 'Log TLS Audit',
  },
  inline: {
    title: '🔍 Inline Inspection', blurb: 'In-process sewing-line inspection — defects caught at the operation while the line is running.',
    opLabel: 'Operation', opPlaceholder: 'e.g. Side seam', showLine: true, lineLabel: 'Line No',
    qtyLabel: 'Qty Checked', defLabel: 'Defects Found', logHeading: 'Log Inline Inspection',
  },
  final: {
    title: '✅ Final Inspection', blurb: 'End-of-line finished-garment inspection before packing — lot offered vs rejects, with AQL accept/reject.',
    opLabel: 'Carton / Lot Ref', opPlaceholder: 'e.g. Cartons 1–20', showLine: false,
    qtyLabel: 'Lot / Sample Checked', defLabel: 'Rejects', logHeading: 'Log Final Inspection',
  },
};

// Who may do what to an inspection (mirrors the server gates in quality.routes.js —
// the SERVER is authoritative; these only decide what to render).
const CAN_EDIT   = ['admin', 'management', 'quality_incharge', 'qc_inspector'];
const CAN_LOCK   = ['admin', 'management', 'quality_incharge'];
const CAN_UNLOCK = ['admin', 'management'];
// RECORDING a material fault and NAMING ITS SOURCE are different acts with different
// consequences: the second starts a vendor claim and, through the mig 205 guard, holds
// that invoice open. Mirrors the backend split (DEFECT_WRITE vs ASSERT_LOT) rather than
// hiding a button over a gate that would still let the request through.
const CAN_RECORD_FAULT = ['admin', 'management', 'quality_incharge', 'qc_inspector',
  'production_incharge', 'ppc_manager', 'stores_incharge'];
const CAN_ASSERT_LOT   = ['admin', 'management', 'quality_incharge'];
const STAGE_OF_TAB = { panel_check: 'cutting', inline: 'stitching', final: 'finishing', tls: 'packing' };

// ---- GPO multi-picker (mig 185) ---------------------------------------------
// gpo_no was free text: 15 of 31 inspections cited a GPO that does not exist and 9 held
// several GPOs in one string, making them invisible to PPM, merch docs and T&A. An
// inspection genuinely can cover several orders, so this picks MANY, each validated.
function GpoPicker({ value, onChange, disabled }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  // Memoised: `value || []` would be a new array identity every render, re-firing the
  // search effect below on each keystroke of the whole form.
  const list = useMemo(() => value || [], [value]);
  useEffect(() => {
    if (!open || q.trim().length < 1) { setHits([]); return; }
    let dead = false;
    const t = setTimeout(async () => {
      const r = await api.get('/quality/gpo-search?q=' + encodeURIComponent(q.trim())).catch(() => null);
      if (!dead && r) setHits((r.data.data || []).filter(g => !list.includes(g.gpo_no)));
    }, 200);
    return () => { dead = true; clearTimeout(t); };
  }, [q, open, list]);
  const add = (g) => { onChange([...list, g.gpo_no]); setQ(''); setHits([]); };
  const drop = (g) => onChange(list.filter(x => x !== g));
  return (
    <div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: list.length ? 6 : 0 }}>
        {list.map(g => (
          <span key={g} style={{ background: '#EEF0FA', color: '#3B3A8F', borderRadius: 12, padding: '2px 8px',
            fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>
            {g}{!disabled && <button type="button" onClick={() => drop(g)}
              style={{ marginLeft: 5, border: 'none', background: 'none', cursor: 'pointer', color: '#3B3A8F' }}>×</button>}
          </span>
        ))}
      </div>
      {!disabled && (
        <div style={{ position: 'relative' }}>
          <input style={{ width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
            placeholder={list.length ? 'Add another GPO…' : 'Search GPO / style / colour…'} value={q}
            onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)}
            onChange={e => { setQ(e.target.value); setOpen(true); }} />
          {open && hits.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, background: '#fff',
              border: '1px solid #ddd', borderRadius: 7, boxShadow: '0 6px 20px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto' }}>
              {hits.map(g => (
                <div key={g.gpo_no} onMouseDown={() => add(g)} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f5f5f5' }}>
                  <strong style={{ fontFamily: 'monospace', color: '#E22D2D' }}>{g.gpo_no}</strong>
                  <span style={{ color: '#888' }}> · {[g.style_no, g.colour].filter(Boolean).join(' · ') || g.item_desc || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const today = () => new Date().toISOString().slice(0, 10);
const pctColor = (p) => p >= 5 ? '#E22D2D' : p >= 2 ? '#854F0B' : '#166534';

// ---- ANSI/ASQ Z1.4 (ISO 2859-1) AQL tables ---------------------------------
// Code letters by lot size — General Inspection Level II.
const CODE_LETTERS = [
  [2, 8, 'A'], [9, 15, 'B'], [16, 25, 'C'], [26, 50, 'D'], [51, 90, 'E'], [91, 150, 'F'],
  [151, 280, 'G'], [281, 500, 'H'], [501, 1200, 'J'], [1201, 3200, 'K'], [3201, 10000, 'L'],
  [10001, 35000, 'M'], [35001, 150000, 'N'], [150001, 500000, 'P'], [500001, Infinity, 'Q'],
];
const SAMPLE_BY_LETTER = { A: 2, B: 3, C: 5, D: 8, E: 13, F: 20, G: 32, H: 50, J: 80, K: 125, L: 200, M: 315, N: 500, P: 800, Q: 1250 };
// Acceptance number Ac per sample size for the common AQL columns (Re = Ac+1).
// Follows the Z1.4 0,1,2,3,5,7,10,14,21 diagonal, shifted one row per AQL step.
const AQL_LEVELS = ['1.0', '1.5', '2.5', '4.0', '6.5'];
const AC_TABLE = {
  2: [0, 0, 0, 0, 0], 3: [0, 0, 0, 0, 0], 5: [0, 0, 0, 0, 1], 8: [0, 0, 0, 1, 1],
  13: [0, 0, 1, 1, 2], 20: [0, 1, 1, 2, 3], 32: [1, 1, 2, 3, 5], 50: [1, 2, 3, 5, 7],
  80: [2, 3, 5, 7, 10], 125: [3, 5, 7, 10, 14], 200: [5, 7, 10, 14, 21],
  315: [7, 10, 14, 21, 21], 500: [10, 14, 21, 21, 21], 800: [14, 21, 21, 21, 21], 1250: [21, 21, 21, 21, 21],
};
function aqlPlan(lotSize, aql) {
  const lot = Math.max(0, parseInt(lotSize) || 0);
  if (lot < 2) return null;
  const row = CODE_LETTERS.find(([lo, hi]) => lot >= lo && lot <= hi);
  if (!row) return null;
  const letter = row[2];
  let sample = SAMPLE_BY_LETTER[letter];
  if (sample > lot) sample = lot; // never sample more than the lot
  const idx = AQL_LEVELS.indexOf(aql);
  const ac = (AC_TABLE[SAMPLE_BY_LETTER[letter]] || [])[idx] ?? 0;
  return { letter, sample, ac, re: ac + 1 };
}

export default function Quality() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('inline');
  const [faultModal, setFaultModal] = useState(null);   // escalate-from-inspection modal
  const isInspection = !!INSP[tab];

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterGpo, setFilterGpo] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // the row being edited (mig 185), or null to create

  // Defect-tracker aggregate state
  const [allRows, setAllRows] = useState([]);

  const cfg = INSP[tab] || {};

  const load = useCallback(async () => {
    if (!INSP[tab]) return;
    const qs = new URLSearchParams({ type: tab });
    if (filterGpo) qs.set('gpo_no', filterGpo);
    if (filterFrom) qs.set('from', filterFrom);
    if (filterTo) qs.set('to', filterTo);
    const r = await api.get('/quality/inspections?' + qs.toString()).catch(() => ({ data: { data: [], summary: {} } }));
    setRows(r.data.data || []); setSummary(r.data.summary || {});
  }, [tab, filterGpo, filterFrom, filterTo]);

  const loadAll = useCallback(async () => {
    const r = await api.get('/quality/inspections').catch(() => ({ data: { data: [] } }));
    setAllRows(r.data.data || []);
  }, []);

  useEffect(() => { if (isInspection) load(); else if (tab === 'defects') loadAll(); }, [tab, isInspection, load, loadAll]);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const openForm = () => { setEditing(null); setForm({ inspection_date: today(), inspector: user?.name || '', gpo_nos: [] }); setError(''); setShowForm(true); };
  // EDIT (mig 185) — the first update path this table has ever had. Locked rows open
  // read-only rather than being hidden, so the record is still inspectable.
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      gpo_nos: r.gpo_nos && r.gpo_nos.length ? r.gpo_nos : (r.gpo_no ? [r.gpo_no] : []),
      style_no: r.style_no || '', line_no: r.line_no || '', operation: r.operation || '',
      inspector: r.inspector || '', inspection_date: (r.inspection_date || '').slice(0, 10),
      qty_checked: r.qty_checked, defects_found: r.defects_found,
      defect_type: r.defect_type || '', action: r.action || '',
    });
    setError(''); setShowForm(true);
  };
  const formPct = num(form.qty_checked) > 0 ? +(num(form.defects_found) / num(form.qty_checked) * 100).toFixed(2) : 0;

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editing) await api.patch(`/quality/inspections/${editing.id}`, form);
      else await api.post('/quality/inspections', { ...form, inspection_type: tab });
      setShowForm(false); setForm({}); setEditing(null); load();
    } catch (err) { setError(err.response?.data?.error || (editing ? 'Failed to save changes' : 'Failed to log inspection')); }
    finally { setSaving(false); }
  };

  const doLock = async (r) => {
    if (!window.confirm(`Lock this ${cfg.title ? cfg.title.replace(/^\S+\s/, '') : 'inspection'}?\n\nGPO ${(r.gpo_nos || [r.gpo_no]).join(', ')} · ${num(r.qty_checked)} checked · ${num(r.defect_pct)}%\n\nOnce locked it cannot be edited. Only admin or management can unlock, and they must give a reason.`)) return;
    try { await api.post(`/quality/inspections/${r.id}/lock`); load(); }
    catch (err) { window.alert(err.response?.data?.error || 'Lock failed'); }
  };
  const doUnlock = async (r) => {
    const reason = askReason('Unlock this inspection?\n\nThe unlock is recorded against your name with this reason.\n\nReason (required):');
    if (reason == null) return;
    try { await api.post(`/quality/inspections/${r.id}/unlock`, { reason: reason.trim() }); load(); }
    catch (err) { window.alert(err.response?.data?.error || 'Unlock failed'); }
  };

  const printList = () => {
    printDocument({
      docTitle: cfg.title?.replace(/^\S+\s/, '') || 'Quality Inspection',
      subtitle: filterGpo ? 'GPO: ' + filterGpo : 'All records',
      meta: [['Inspections', summary.inspections || rows.length], ['Defect %', (summary.defect_pct ?? 0) + '%']],
      sections: [{
        table: {
          columns: ['Date', 'GPO', 'Style', cfg.showLine ? (cfg.lineLabel || 'Line') : '', cfg.opLabel || 'Operation', 'Inspector', cfg.qtyLabel || 'Checked', cfg.defLabel || 'Defects', 'Defect %', 'Type', 'Action'].filter(Boolean),
          rows: rows.map(r => [fmtDate(r.inspection_date), r.gpo_no, r.style_no || '—', cfg.showLine ? (r.line_no || '—') : null, r.operation || '—', r.inspector || '—', num(r.qty_checked), num(r.defects_found), num(r.defect_pct) + '%', r.defect_type || '—', r.action || '—'].filter(x => x !== null)),
        },
      }],
    });
  };

  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
  const card = { background: 'white', borderRadius: 8, padding: '12px 16px', boxShadow: 'none', border: '1px solid #E8E7E7' };

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      

      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#1a1a2e' }}>🛡 Quality Control</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>Panel check, TLS audit, inline & final inspection, AQL sampling, and defect tracking across orders.</p>
        </div>

        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', padding: '0 12px', flexWrap: 'wrap' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '14px 18px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13,
                fontWeight: tab === t.key ? 600 : 400, color: tab === t.key ? '#3B3A8F' : '#888',
                borderBottom: tab === t.key ? '2px solid #3B3A8F' : '2px solid transparent', marginBottom: -1, whiteSpace: 'nowrap' }}>{t.label}</button>
            ))}
          </div>

          <div style={{ padding: 20 }}>
            {/* ---- Inspection tabs (panel / tls / inline / final) ---- */}
            {isInspection && (
              <div>
                <p style={{ margin: '0 0 16px', fontSize: 12, color: '#999' }}>{cfg.blurb}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
                  {[
                    { label: 'Inspections', value: summary.inspections || 0, color: '#3B3A8F' },
                    { label: cfg.qtyLabel || 'Qty Checked', value: num(summary.qty_checked).toLocaleString('en-IN'), color: '#3B3A8F' },
                    { label: cfg.defLabel || 'Defects', value: num(summary.defects).toLocaleString('en-IN'), color: '#BA7517' },
                    { label: 'Defect %', value: (summary.defect_pct ?? 0) + '%', color: pctColor(num(summary.defect_pct)) },
                  ].map((s, i) => <KpiCard key={i} label={s.label} value={s.value} color={s.color} />)}
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                  <input placeholder="Filter by GPO No..." value={filterGpo} onChange={e => setFilterGpo(e.target.value)} style={{ ...inputStyle, width: 180 }} />
                  <label style={{ fontSize: 12, color: '#888' }}>From <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={{ ...inputStyle, width: 150, display: 'inline-block', marginLeft: 4 }} /></label>
                  <label style={{ fontSize: 12, color: '#888' }}>To <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} style={{ ...inputStyle, width: 150, display: 'inline-block', marginLeft: 4 }} /></label>
                  {(filterGpo || filterFrom || filterTo) && <button onClick={() => { setFilterGpo(''); setFilterFrom(''); setFilterTo(''); }} style={{ padding: '6px 12px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Clear</button>}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {rows.length > 0 && <button onClick={printList} style={printBtnStyle}>🖨 Print</button>}
                    <button onClick={openForm} style={{ padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>+ {cfg.logHeading}</button>
                  </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ background: '#F7F6F3' }}>
                    {['Date', 'GPO No', 'Style', cfg.showLine ? (cfg.lineLabel || 'Line') : null, cfg.opLabel, 'Inspector', cfg.qtyLabel, cfg.defLabel, 'Defect %', 'Defect Type', 'Action', ''].filter(Boolean).map((h, hi) => (
                      <th key={h + hi} style={{ textAlign: 'left', padding: '8px 10px', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {rows.length === 0 ? <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No records logged{filterGpo || filterFrom || filterTo ? ' for this filter' : ''}.</td></tr>
                      : rows.map(r => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5', background: r.is_locked ? '#FBFBF8' : undefined }}>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDate(r.inspection_date)}</td>
                          {/* Every GPO the inspection covers, not just the first. A row whose
                              legacy text never resolved shows the raw text with a warning. */}
                          <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>
                            {(r.gpo_nos && r.gpo_nos.length)
                              ? r.gpo_nos.map(g => <div key={g}>{g}</div>)
                              : <span title="This GPO number does not match any order — edit the record to pick a real GPO."
                                  style={{ color: '#A32D2D' }}>⚠ {r.gpo_no}</span>}
                          </td>
                          <td style={{ padding: '8px 10px' }}>{r.style_no || '—'}</td>
                          {cfg.showLine && <td style={{ padding: '8px 10px' }}>{r.line_no || '—'}</td>}
                          <td style={{ padding: '8px 10px' }}>{r.operation || '—'}</td>
                          <td style={{ padding: '8px 10px' }}>{r.inspector || '—'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>{num(r.qty_checked).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: num(r.defects_found) > 0 ? '#E22D2D' : '#888' }}>{num(r.defects_found)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: pctColor(num(r.defect_pct)) }}>{num(r.defect_pct)}%</td>
                          <td style={{ padding: '8px 10px' }}>{r.defect_type || '—'}</td>
                          <td style={{ padding: '8px 10px' }}>{r.action || '—'}</td>
                          {/* Edit until locked; lock is the QA manager's terminus. */}
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            {r.is_locked ? (
                              <>
                                <span title={`Locked by ${r.locked_by_name || '—'} on ${fmtDate(r.locked_at)}`}
                                  style={{ fontSize: 11, color: '#166534', fontWeight: 700 }}>🔒 Locked</span>
                                {CAN_UNLOCK.includes(user?.role) && (
                                  <button onClick={() => doUnlock(r)} title="Unlock — admin/management only, reason required"
                                    style={{ marginLeft: 6, padding: '3px 8px', fontSize: 10, background: '#FCEBEB', color: '#A32D2D', border: '1px solid #f0cccc', borderRadius: 5, cursor: 'pointer' }}>Unlock</button>
                                )}
                              </>
                            ) : (
                              <>
                                {CAN_EDIT.includes(user?.role) && (
                                  <button onClick={() => openEdit(r)}
                                    style={{ padding: '3px 10px', fontSize: 11, background: '#EEF0FA', color: '#3B3A8F', border: '1px solid #dfe3f5', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                                )}
                                {CAN_LOCK.includes(user?.role) && (
                                  <button onClick={() => doLock(r)} title="Lock this record — no further edits"
                                    style={{ marginLeft: 6, padding: '3px 8px', fontSize: 10, background: '#EAF7EE', color: '#166534', border: '1px solid #cfe8d6', borderRadius: 5, cursor: 'pointer' }}>🔒 Lock</button>
                                )}
                                {/* ESCALATE. Panel Check is the fabric-defect check at cutting and Inline is the
                                    stitching line, so a material fault is usually already recorded here as an
                                    inspection. Raising it re-uses those facts rather than asking for them twice —
                                    a second near-identical form is how the second one stops being filled. */}
                                {CAN_RECORD_FAULT.includes(user?.role) && num(r.defects_found) > 0 && STAGE_OF_TAB[tab] && (
                                  <button onClick={() => setFaultModal({
                                      inspection_id: r.id, stage: STAGE_OF_TAB[tab], gpo_no: r.gpo_no || '',
                                      qty: String(num(r.defects_found)), uom: 'PCS',
                                      defect_type: r.defect_type || '', outcome: 'consumed',
                                      estimated_value: '', found_at: '', err: '' })}
                                    title="Record this as a material fault — a supplier problem, not a stitching one"
                                    style={{ marginLeft: 6, padding: '3px 8px', fontSize: 10, background: '#FFF8E6', color: '#B45309', border: '1px solid #f0dcb0', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>⚠ Material fault</button>
                                )}
                                {r.updated_at && <span title={`Last edited by ${r.updated_by_name || '—'}`} style={{ marginLeft: 6, fontSize: 10, color: '#999' }}>edited</span>}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <ListFooter shown={rows.length} total={rows.length} noun="inspections" />
              </div>
            )}

            {/* ---- AQL Calculator ---- */}
            {tab === 'aql' && <AQLCalculator />}

            {/* ---- Defect Tracker ---- */}
            {tab === 'defects' && <DefectTracker rows={allRows} />}

            {/* ---- Raw Material Inward (Deroi fabric dispatches, 4-Point review) ---- */}
            {tab === 'rm_inward' && <RawMaterialInward user={user} />}
            {tab === 'greige_qc' && <GreigeQC />}
            {tab === 'store_qc' && <StoreReceiptQC user={user} />}
            {tab === 'material_faults' && <MaterialFaults user={user} />}
            {faultModal && <FaultModal m={faultModal} setM={setFaultModal} onSaved={() => { setFaultModal(null); setTab('material_faults'); }} />}
          </div>
        </div>
      </div>

      {showForm && isInspection && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{cfg.title} — {editing ? 'Edit Inspection' : cfg.logHeading}</h3></div>
            {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{error}</div>}
            {editing && (
              <div style={{ background: '#F7F6F3', border: '1px solid #eee', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#666' }}>
                Editing a record logged by <strong>{editing.created_by_name || '—'}</strong> on {fmtDate(editing.created_at)}.
                Every change is recorded against your name, with the before and after values.
              </div>
            )}
            <form onSubmit={submit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {/* GPO is a validated pick, and an inspection may cover several orders. */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>GPO No * <span style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>— pick one or more; typing a GPO that does not exist is what made 15 records invisible downstream</span></label>
                  <GpoPicker value={form.gpo_nos} onChange={v => setForm({ ...form, gpo_nos: v })} />
                </div>
                <div><label style={labelStyle}>Style</label><input style={inputStyle} value={form.style_no || ''} onChange={e => setForm({ ...form, style_no: e.target.value })} /></div>
                {cfg.showLine && <div><label style={labelStyle}>{cfg.lineLabel || 'Line No'}</label><input style={inputStyle} value={form.line_no || ''} onChange={e => setForm({ ...form, line_no: e.target.value })} /></div>}
                <div><label style={labelStyle}>{cfg.opLabel}</label><input style={inputStyle} placeholder={cfg.opPlaceholder} value={form.operation || ''} onChange={e => setForm({ ...form, operation: e.target.value })} /></div>
                <div><label style={labelStyle}>Inspector *</label><input style={inputStyle} required value={form.inspector || ''} onChange={e => setForm({ ...form, inspector: e.target.value })} /></div>
                <div><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.inspection_date || today()} onChange={e => setForm({ ...form, inspection_date: e.target.value })} /></div>
                <div><label style={labelStyle}>{cfg.qtyLabel} *</label><input type="number" min="0" style={inputStyle} required value={form.qty_checked || ''} onChange={e => setForm({ ...form, qty_checked: e.target.value })} /></div>
                <div><label style={labelStyle}>{cfg.defLabel}</label><input type="number" min="0" style={inputStyle} value={form.defects_found || ''} onChange={e => setForm({ ...form, defects_found: e.target.value })} /></div>
                <div><label style={labelStyle}>Defect Type</label><input style={inputStyle} placeholder="e.g. Broken stitch" value={form.defect_type || ''} onChange={e => setForm({ ...form, defect_type: e.target.value })} /></div>
                <div><label style={labelStyle}>Action</label><input style={inputStyle} placeholder="e.g. Re-work / Reject" value={form.action || ''} onChange={e => setForm({ ...form, action: e.target.value })} /></div>
              </div>
              <div style={{ marginTop: 14, padding: '10px 14px', background: '#F7F6F3', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#666' }}>Auto-calculated Defect %</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: pctColor(formPct) }}>{formPct}%</span>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving || !(form.gpo_nos || []).length} style={{ padding: '8px 18px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600, opacity: (form.gpo_nos || []).length ? 1 : 0.5 }}>{saving ? 'Saving...' : (editing ? 'Save changes' : 'Save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ AQL Calculator ============================
function AQLCalculator() {
  const [lot, setLot] = useState('1000');
  const [critical, setCritical] = useState('1.0');
  const [major, setMajor] = useState('2.5');
  const [minor, setMinor] = useState('4.0');
  const [found, setFound] = useState({ critical: '', major: '', minor: '' });

  const plans = {
    critical: aqlPlan(lot, critical),
    major: aqlPlan(lot, major),
    minor: aqlPlan(lot, minor),
  };
  const sample = plans.major?.sample || plans.minor?.sample || plans.critical?.sample || 0;

  const verdict = (key) => {
    const p = plans[key]; const f = parseInt(found[key]);
    if (!p || found[key] === '' || isNaN(f)) return null;
    return f <= p.ac ? 'ACCEPT' : 'REJECT';
  };
  const overall = ['critical', 'major', 'minor'].map(verdict);
  const decided = overall.some(v => v !== null);
  const finalVerdict = decided ? (overall.includes('REJECT') ? 'REJECTED' : 'ACCEPTED') : null;

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
  const sel = (val, set) => (
    <select value={val} onChange={e => set(e.target.value)} style={inputStyle}>
      {AQL_LEVELS.map(l => <option key={l} value={l}>AQL {l}</option>)}
    </select>
  );

  const ROWS = [
    { key: 'critical', label: 'Critical', aql: critical, color: '#E22D2D' },
    { key: 'major', label: 'Major', aql: major, color: '#854F0B' },
    { key: 'minor', label: 'Minor', aql: minor, color: '#0891b2' },
  ];

  return (
    <div>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#999' }}>
        ANSI/ASQ Z1.4 (ISO 2859-1) single sampling · General Inspection Level II · Normal severity. Enter the lot size to get the sample size and accept/reject limits.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20, maxWidth: 720 }}>
        <div><label style={labelStyle}>Lot / Batch Size</label><input type="number" min="1" value={lot} onChange={e => setLot(e.target.value)} style={inputStyle} /></div>
        <div><label style={labelStyle}>Critical AQL</label>{sel(critical, setCritical)}</div>
        <div><label style={labelStyle}>Major AQL</label>{sel(major, setMajor)}</div>
        <div><label style={labelStyle}>Minor AQL</label>{sel(minor, setMinor)}</div>
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ background: '#EEEDF8', borderRadius: 10, padding: '14px 20px' }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Code Letter</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#3B3A8F' }}>{plans.major?.letter || '—'}</div>
        </div>
        <div style={{ background: '#EEEDF8', borderRadius: 10, padding: '14px 20px' }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Sample Size</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#3B3A8F' }}>{sample || '—'}</div>
        </div>
      </div>

      <table style={{ width: '100%', maxWidth: 760, borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['Defect Class', 'AQL', 'Sample', 'Accept (≤)', 'Reject (≥)', 'Defects Found', 'Result'].map(h => (
            <th key={h} style={{ textAlign: 'left', padding: '9px 12px', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11 }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {ROWS.map(r => {
            const p = plans[r.key]; const v = verdict(r.key);
            return (
              <tr key={r.key} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: '9px 12px', fontWeight: 700, color: r.color }}>{r.label}</td>
                <td style={{ padding: '9px 12px' }}>{r.aql}</td>
                <td style={{ padding: '9px 12px' }}>{p?.sample ?? '—'}</td>
                <td style={{ padding: '9px 12px', color: '#166534', fontWeight: 600 }}>{p?.ac ?? '—'}</td>
                <td style={{ padding: '9px 12px', color: '#E22D2D', fontWeight: 600 }}>{p?.re ?? '—'}</td>
                <td style={{ padding: '9px 12px' }}>
                  <input type="number" min="0" value={found[r.key]} onChange={e => setFound({ ...found, [r.key]: e.target.value })} style={{ width: 70, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 5, fontSize: 13 }} />
                </td>
                <td style={{ padding: '9px 12px' }}>{v && <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: v === 'ACCEPT' ? '#EAF7EE' : '#FCEBEB', color: v === 'ACCEPT' ? '#166534' : '#A32D2D' }}>{v}</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {finalVerdict && (
        <div style={{ marginTop: 16, padding: '16px 24px', borderRadius: 10, textAlign: 'center', background: finalVerdict === 'ACCEPTED' ? '#EAF7EE' : '#FCEBEB', maxWidth: 760 }}>
          <span style={{ fontSize: 13, color: '#666' }}>Lot Decision: </span>
          <span style={{ fontSize: 22, fontWeight: 800, color: finalVerdict === 'ACCEPTED' ? '#166534' : '#A32D2D' }}>{finalVerdict === 'ACCEPTED' ? '✅ LOT ACCEPTED' : '❌ LOT REJECTED'}</span>
        </div>
      )}
    </div>
  );
}

// ============================ Raw Material Inward ============================
// Fabric dispatches from Deroi Mart pending RSR acceptance. RSR quality reviews
// the 4-Point report and Accepts (GRN auto-generated) / Accepts with Condition /
// Rejects (auto-notification to Deroi with the reason).
// Fabric Q&Q document checklist — must mirror FABRIC_QQ_DOCS on the backend.
const QQ_DOCS = [
  ['invoice', 'Invoice received'],
  ['packing_list', 'Packing List received'],
  ['four_point_report', '4-Point Inspection Report received'],
  ['gsm_card', 'GSM Card received'],
  ['csv_card', 'CSV Card (Shade) received'],
  ['shrinkage_report', 'Shrinkage Report received'],
];
// Grades are shown as the seller declared them AND as their rolls compute, whenever the two
// differ — so an accepted downgrade reads as the disclosure it is rather than a bare letter.
const gradeLabel = (rep) => {
  const d = (rep || {}).final_grade, c = (rep || {}).computed_final_grade;
  if (!d && !c) return '—';
  if (!d || !c || d === c) return d || c;
  return `${effectiveGrade(rep)} — Deroi declared ${d}, their rolls compute ${c}`;
};

// The PO's unit in words. A metre reading is not a weighment, and the box must not say KG
// when the order is in metres — that is the label that misleads the person typing into it.
const uomWord = (u) => (u === 'meters' ? 'MTR' : u === 'kilograms' ? 'KG' : (u || '—'));

const blankQQ = { docs: {}, gsm_actual: '', width_actual: '', shade: '', overall: '', weighment: '', rolls_received: '' };

// ── TRANSFER PRICE, ENTERED BY THE BUYER, WITH THE COST BESIDE IT (mig 258) ──────────────
// Shared by the Q&Q review and the deviation approval because both are acceptances and both
// bill RSR. One component so the two cannot drift into disagreeing about what a price is.
//
// The field is NEVER pre-filled — not from Deroi's ask, not from the cost figure. A box that
// arrives already populated is a decision nobody took, and the cost here is yarn material
// only, so pre-filling from it would be wrong twice over.
// `locked` renders the rate as a FACT rather than a field. Used on the deviation approval once
// the price was set when the deviation was raised: the approver is signing off QUALITY, and a
// live price box there invites a commercial decision the two-person rule was never about.
function PricePanel({ view, rate, setRate, costRef, locked }) {
  const inp = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
  const lbl = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
  return (
    <div style={{ background: '#F7F7FB', border: '1px solid #E3E2F0', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Transfer price — RSR enters or confirms</div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 175 }}>
          <label style={lbl}>Rate RSR will be billed (₹/KG){locked ? '' : ' *'}</label>
          {locked
            ? <div style={{ ...inp, background: '#EAF7EE', border: '1px solid #BFE3CB', color: '#166534', fontWeight: 700 }}>
                ₹{rate}<span style={{ fontWeight: 400, color: '#4B7A5A', fontSize: 11 }}> — set when the deviation was raised</span>
              </div>
            : <input style={inp} type="number" step="0.0001" value={rate} placeholder="not set" onChange={e => setRate(e.target.value)} />}
          <div style={{ fontSize: 10.5, color: '#888', marginTop: 4 }}>
            {view?.dispatch_rate_proposed
              ? <>Deroi proposes <strong>₹{view.dispatch_rate_proposed}/kg</strong> — their ask, not the price.</>
              : 'Deroi has proposed no rate.'}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 230, fontSize: 12, color: '#555' }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: '#6B4E00' }}>Cost reference</div>
          {view?.po_unit && view.po_unit !== 'kilograms' ? (
            <div style={{ color: '#8A6D00' }}>
              No per-{view.po_unit === 'meters' ? 'metre' : 'unit'} cost basis — the ledger measures yarn in kg.
              <div style={{ marginTop: 4, fontSize: 11, color: '#888' }}>
                Nothing is shown rather than a per-kg figure, which would read about 4.5x the per-metre price on this cloth.
              </div>
            </div>
          ) : costRef ? (<>
            <div>This GPO: <strong>{costRef.cost_per_kg != null ? `₹${costRef.cost_per_kg}/kg` : 'no cost recorded'}</strong>
              {costRef.ledger_rows ? ` · ${costRef.ledger_rows} ledger row(s)` : ''}</div>
            <div>All costed GPOs: <strong>{costRef.all_gpos_cost_per_kg != null ? `₹${costRef.all_gpos_cost_per_kg}/kg` : '—'}</strong>
              {costRef.all_gpos ? ` across ${costRef.all_gpos}` : ''}</div>
            {costRef.caveat && <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 11, color: '#8A6D00' }}>{costRef.caveat}</div>}
          </>) : <div style={{ color: '#999' }}>—</div>}
          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: '#8A6D00' }}>Reference only — not a floor, not a suggestion, never filled in for you.</div>
        </div>
      </div>
    </div>
  );
}

function RawMaterialInward({ user }) {
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [qq, setQQ] = useState(blankQQ);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const canReview = ['admin', 'management', 'qc_inspector', 'procurement_officer'].includes(user?.role);
  const canApproveDeviation = ['admin', 'management'].includes(user?.role);

  const load = useCallback(async () => {
    const r = await api.get('/procurement/deroi-dispatch').catch(() => ({ data: { data: [] } }));
    setList(r.data.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // WHEN AN ACCEPTANCE IS REFUSED FOR WANT OF A PRICE, SHOW THE COST. Refusing without
  // showing the only cost figure asks somebody to price blind, and pricing blind is how an
  // arbitrary transfer price gets typed in to make the error go away. The backend sends the
  // reference with the 409 so it appears at the moment of refusal, not one screen away.
  const [priceHelp, setPriceHelp] = useState(null);

  // THE TRANSFER PRICE IS THE BUYER'S (mig 258). Deroi proposes on the dispatch; RSR enters or
  // confirms here, at the moment of acceptance. Never pre-filled — not from Deroi's ask and
  // not from the cost reference — because a pre-filled price is a decision nobody took.
  const [rate, setRate] = useState('');
  const [costRef, setCostRef] = useState(null);

  // Fetched when the dispatch is opened, so the cost sits BESIDE the field being typed into
  // rather than appearing only after a refusal.
  useEffect(() => {
    if (!view) { setCostRef(null); setRate(''); return; }
    // PREFILL FROM THE STORED RATE. This blanked unconditionally, while the server treats an
    // omitted rate as "use the one on the dispatch" (resolveAcceptRate). So a dispatch that was
    // already priced showed an empty box and a greyed button, and the approver had to retype a
    // price the record already held. Harmless while every rate was null; load-bearing now that
    // a deviation is priced when it is RAISED.
    setRate(Number(view.dispatch_rate) > 0 ? String(view.dispatch_rate) : '');
    // NOT FETCHED AT ALL when the order is not in kilograms. The ledger divides amount by kg,
    // so the figure is Rs/kg; beside a box asking for Rs/metre it would mislead by roughly 4.5x
    // on this cloth. An absent figure with a reason is honest; a wrong-unit one is not.
    if (view.po_unit && view.po_unit !== 'kilograms') { setCostRef(null); return; }
    let live = true;
    api.get('/procurement/deroi-dispatch/cost-reference', { params: { gpo_no: view.gpo_no } })
      .then(r => { if (live) setCostRef(r.data.data || null); })
      .catch(() => { if (live) setCostRef(null); });
    return () => { live = false; };
  }, [view]);

  const review = async (decision, qqPayload) => {
    if ((decision === 'reject' || decision === 'request_deviation') && !remarks) {
      setError(decision === 'reject' ? 'Remarks are required to reject.' : 'A written deviation reason is required.');
      return;
    }
    setBusy(true); setError(''); setPriceHelp(null);
    try {
      const r = await api.patch(`/procurement/deroi-dispatch/${view.id}/review`, {
        decision, remarks, qq_check: qqPayload,
        ...(rate === '' ? {} : { dispatch_rate: Number(rate) }),
      });
      setView(null); setRemarks(''); setQQ(blankQQ); load();
      if (r.data.deviation_pending) alert('🛡 Deviation requested — a second person (management) must approve before the GRN is generated.');
      else if (decision !== 'reject') alert(`✅ Accepted — GRN ${r.data.grn_no} generated.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Review failed');
      setPriceHelp(err.response?.data?.code === 'NO_TRANSFER_PRICE' ? (err.response.data.cost_reference || {}) : null);
    }
    finally { setBusy(false); }
  };

  // Second-person sign-off on a deviation_pending dispatch (management/admin, ≠ requester).
  const approveDeviation = async (approve) => {
    if (approve === false && !remarks) { setError('A reason is required to reject the deviation.'); return; }
    setBusy(true); setError(''); setPriceHelp(null);
    try {
      const r = await api.patch(`/procurement/deroi-dispatch/${view.id}/deviation-approve`, {
        approve, remarks, ...(rate === '' ? {} : { dispatch_rate: Number(rate) }),
      });
      setView(null); setRemarks(''); load();
      alert(approve ? `✅ Deviation approved — GRN ${r.data.grn_no} generated.` : '❌ Deviation rejected — dispatch sent back to Deroi.');
    } catch (err) {
      setError(err.response?.data?.error || 'Deviation action failed');
      setPriceHelp(err.response?.data?.code === 'NO_TRANSFER_PRICE' ? (err.response.data.cost_reference || {}) : null);
    }
    finally { setBusy(false); }
  };

  const DD_STATUS = {
    pending: { bg: '#FFF8E6', c: '#9A6B00', label: 'Pending Review' },
    accepted: { bg: '#EAF7EE', c: '#166534', label: 'Accepted' },
    rejected: { bg: '#FCEBEB', c: '#A32D2D', label: 'Rejected' },
    deviation_pending: { bg: '#FEF3E2', c: '#B45309', label: '🛡 Deviation — awaiting approval' },
    accepted_with_deviation: { bg: '#EAF7EE', c: '#166534', label: '✅ Accepted (deviation)' },
  };
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const card = { background: 'white', borderRadius: 8, padding: '12px 16px', boxShadow: 'none', border: '1px solid #E8E7E7' };
  const kv = (k, v) => <div style={{ fontSize: 12 }}><span style={{ color: '#888' }}>{k}: </span><strong>{v ?? '—'}</strong></div>;
  const rep = view ? (typeof view.inspection_report_json === 'string' ? JSON.parse(view.inspection_report_json) : view.inspection_report_json) || {} : {};
  const hdr = rep.header || {}; const rolls = rep.rolls || [];
  const secStyle = { fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, margin: '16px 0 8px', paddingBottom: 4, borderBottom: '1px solid #EEEDF8' };

  const printReport = () => {
    printDocument({
      docTitle: '4-Point Fabric Inspection Report',
      subtitle: `${view.po_no} · GPO ${view.gpo_no || '—'} · Invoice ${view.invoice_no}`,
      meta: [['Status', DD_STATUS[view.status]?.label], ['GRN', view.grn_no || '—'], ['Final Grade', gradeLabel(rep)]],
      sections: [
        { title: 'Documents', fields: [['Invoice No', view.invoice_no], ['Invoice Date', fmtD(view.invoice_date)], ['Invoice Amount', '₹' + Number(view.invoice_amount || 0).toLocaleString('en-IN')], ['Packing List No', view.packing_list_no], ['Total Rolls', view.total_rolls], [`Total qty (${uomWord(view.po_unit)})`, view.total_qty]] },
        { title: 'Report Header', fields: [['Fabric Desc', hdr.fabric_desc], ['GPO / PO', hdr.gpo_po], ['Challan No & Date', [hdr.challan_no, fmtD(hdr.challan_date)].filter(Boolean).join(' · ')], ['Knitting M/c No', hdr.knitting_mc_no], ['Colour', hdr.colour], ['Buyer', hdr.buyer], ['Challan Qty (KG)', hdr.challan_qty_kg], ['Color Matching', hdr.color_matching], ['Req GSM', hdr.req_gsm], ['Merchant', hdr.merchant_name], ['Rolls Received/Checked', hdr.rolls_received_checked], ['Req Width', hdr.req_width], ['Process Type', hdr.process_type], ['Checker Name/ID', hdr.checker_name_id]] },
        { title: 'Roll-wise Inspection', table: { columns: ['Roll', 'GSM', 'Width″', 'Len m', ...DEFECT_TYPES.map(d => d.split(' ')[0]), 'Pts', 'Grade'], rows: rolls.map(r => [r.roll_no, r.gsm_actual, r.width_in, r.length_m, ...DEFECT_TYPES.map(d => r.defects?.[d] || 0), r.total_points, r.grade]) } },
        { title: 'Shrinkage & Tests', fields: [['Length wise %', rep.shrinkage?.length_pct], ['Width wise %', rep.shrinkage?.width_pct], ['Torque', rep.shrinkage?.torque], ['PH Value', rep.tests?.ph_value], ['Dry Rubbing', rep.tests?.dry_rubbing], ['Wet Rubbing', rep.tests?.wet_rubbing], ['Colour Fastening', rep.tests?.colour_fastening], ['Bonding Strength', rep.tests?.bonding_strength], ['Special Finishing', rep.special_finishing], ['Swatch Attached', rep.swatch_attached]] },
        { title: 'Signatures', fields: [['Checker', rep.signatures?.checker], ['Quality Executive', rep.signatures?.quality_executive], ['Fabric Incharge', rep.signatures?.fabric_incharge], ['Merchant (Deroi)', rep.signatures?.merchant], ['Fabric Incharge Remarks', rep.fabric_incharge_remarks]] },
      ],
    });
  };

  return (
    <div>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#999' }}>Fabric dispatches from Deroi Mart pending RSR acceptance — review the 4-Point report, then Accept (auto-GRN), Accept with Condition, or Reject (Deroi is notified with the reason).</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16, maxWidth: 600 }}>
        {[['Pending', list.filter(d => d.status === 'pending').length, '#9A6B00'], ['Accepted', list.filter(d => d.status === 'accepted').length, '#166534'], ['Rejected', list.filter(d => d.status === 'rejected').length, '#A32D2D']].map(([l, v, c]) => (
          <div key={l} style={card}><div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{l}</div><div style={{ fontSize: 24, fontWeight: 700, color: c }}>{v}</div></div>
        ))}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#F7F6F3' }}>
          {['Submitted', 'PO No', 'GPO', 'Invoice', 'Packing List', 'Rolls', 'Qty', 'Final Grade', 'Status', 'GRN', ''].map(h => (
            <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {list.length === 0 ? <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No fabric dispatches from Deroi yet.</td></tr>
            : list.map(d => {
              const st = DD_STATUS[d.status] || DD_STATUS.pending;
              // The worse of the two — the queue must not show A for a lot the gates will
              // treat as D, or the list and the modal would disagree about the same dispatch.
              const g = effectiveGrade(typeof d.inspection_report_json === 'string' ? JSON.parse(d.inspection_report_json) : d.inspection_report_json);
              return (
                <tr key={d.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtD(d.submitted_at)}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{d.po_no}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{d.gpo_no || '—'}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{d.invoice_no}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{d.packing_list_no}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{d.total_rolls}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{/* total_qty in the PO's own unit (mig 260) — total_kg no longer exists and read as 0 here. */}{Number(d.total_qty || 0).toLocaleString('en-IN')} {uomWord(d.po_unit)}</td>
                  <td style={{ padding: '8px 10px' }}>{g && <span style={{ padding: '2px 10px', borderRadius: 8, fontSize: 11, fontWeight: 800, background: (GRADE_COLOR[g] || '#888') + '22', color: GRADE_COLOR[g] || '#888' }}>{g}</span>}</td>
                  <td style={{ padding: '8px 10px' }}><span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: st.bg, color: st.c }}>{st.label}{d.accepted_with_condition ? ' *' : ''}</span></td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#166534' }}>{d.grn_no || '—'}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <button onClick={() => { setView(d); setRemarks(''); setError(''); setQQ(blankQQ); }} style={{ padding: '4px 12px', background: d.status === 'deviation_pending' ? '#FEF3E2' : '#EEEDF8', border: 'none', borderRadius: 4, color: d.status === 'deviation_pending' ? '#B45309' : '#3B3A8F', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                      {d.status === 'pending' && canReview ? '🔍 Q&Q Check'
                        : d.status === 'deviation_pending' && canApproveDeviation ? '🛡 Review Deviation'
                        : d.status === 'deviation_pending' ? '🛡 Deviation'
                        : '👁 View'}</button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      <ListFooter shown={list.length} total={list.length} noun="dispatches" />

      {view && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
          <div style={{ background: 'white', borderRadius: 14, width: '92%', maxWidth: 1000, margin: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ background: '#2D2C72', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: 1 }}>4-Point Fabric Inspection · Deroi Dispatch</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'white', marginTop: 2 }}>{view.po_no} · GPO {view.gpo_no || '—'}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>Submitted {fmtD(view.submitted_at)} by {view.submitted_by_name || 'Deroi'} · Invoice {view.invoice_no}</div>
              </div>
              <button onClick={() => setView(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '16px 24px 24px' }}>
              {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
              {priceHelp && (
                <div style={{ background: '#FFF8E6', border: '1px solid #F0E0B0', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: '#6B4E00' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Cost reference — for pricing this dispatch</div>
                  <div>This GPO: <strong>{priceHelp.cost_per_kg != null ? `₹${priceHelp.cost_per_kg}/kg` : 'no cost recorded'}</strong>
                    {priceHelp.ledger_rows ? ` · ${priceHelp.ledger_rows} ledger row(s)` : ''}
                    {priceHelp.kg ? ` · ${Number(priceHelp.kg).toFixed(3)} kg costed` : ''}</div>
                  <div>All costed GPOs: <strong>{priceHelp.all_gpos_cost_per_kg != null ? `₹${priceHelp.all_gpos_cost_per_kg}/kg` : '—'}</strong>
                    {priceHelp.all_gpos ? ` across ${priceHelp.all_gpos} GPO(s)` : ''}</div>
                  {priceHelp.caveat && <div style={{ marginTop: 6, fontStyle: 'italic' }}>{priceHelp.caveat}</div>}
                  <div style={{ marginTop: 6, fontWeight: 600 }}>Reference only — it is not a floor, not a suggested price, and it is never filled in for you.</div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={secStyle}>Invoice</div>
                  {kv('No', view.invoice_no)}{kv('Date', fmtD(view.invoice_date))}{kv('Amount', '₹' + Number(view.invoice_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }))}
                </div>
                <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={secStyle}>Packing List</div>
                  {kv('No', view.packing_list_no)}{kv('Total Rolls', view.total_rolls)}{kv(`Total qty (${uomWord(view.po_unit)})`, Number(view.total_qty || 0).toLocaleString('en-IN'))}
                </div>
                <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={secStyle}>Result</div>
                  {kv('Final Grade', gradeLabel(rep))}{kv('Status', (DD_STATUS[view.status] || {}).label + (view.accepted_with_condition ? ' (with condition)' : ''))}{kv('GRN', view.grn_no)}
                  {view.rsr_remarks && kv('RSR Remarks', view.rsr_remarks)}
                </div>
              </div>

              <div style={secStyle}>Report Header</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '6px 16px' }}>
                {kv('Fabric Desc', hdr.fabric_desc)}{kv('GPO / PO', hdr.gpo_po)}{kv('Challan No', hdr.challan_no)}{kv('Challan Date', fmtD(hdr.challan_date))}
                {kv('Knitting M/c No', hdr.knitting_mc_no)}{kv('Colour', hdr.colour)}{kv('Buyer', hdr.buyer)}{kv('Challan Qty (KG)', hdr.challan_qty_kg)}
                {kv('Color Matching', hdr.color_matching)}{kv('Req GSM', hdr.req_gsm)}{kv('Merchant', hdr.merchant_name)}{kv('Rolls Recd/Checked', hdr.rolls_received_checked)}
                {kv('GSM Card', [hdr.gsm_card_no, fmtD(hdr.gsm_card_date)].filter(x => x && x !== '—').join(' · '))}{kv('Req Width', hdr.req_width)}{kv('Start–End', [hdr.start_time, hdr.end_time].filter(Boolean).join(' – '))}{kv('Process Type', hdr.process_type)}
                {kv('CSV Card', [hdr.csv_card_no, fmtD(hdr.csv_card_date)].filter(x => x && x !== '—').join(' · '))}{kv('Shrinkage Report', [hdr.shrinkage_report_no, fmtD(hdr.shrinkage_report_date)].filter(x => x && x !== '—').join(' · '))}{kv('Checker Name/ID', hdr.checker_name_id)}
              </div>

              <div style={secStyle}>Roll-wise Inspection ({rolls.length} rolls)</div>
              <div style={{ fontSize: 11, color: '#854F0B', background: '#FAF7EE', padding: '6px 10px', borderRadius: 6, marginBottom: 8 }}>{POINT_RULES}</div>
              <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 1100, width: '100%' }}>
                  <thead><tr style={{ background: '#F7F6F3' }}>
                    {['Roll', 'GSM', 'Width″', 'Len m', ...DEFECT_TYPES, 'Total', 'Grade', 'Remarks'].map(h => (
                      <th key={h} style={{ padding: '6px 6px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 9, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {rolls.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '5px 6px', fontWeight: 700 }}>{r.roll_no}</td>
                        <td style={{ padding: '5px 6px' }}>{r.gsm_actual || '—'}</td>
                        <td style={{ padding: '5px 6px' }}>{r.width_in || '—'}</td>
                        <td style={{ padding: '5px 6px' }}>{r.length_m || '—'}</td>
                        {DEFECT_TYPES.map(d => {
                          const v = parseFloat(r.defects?.[d]) || 0;
                          return <td key={d} style={{ padding: '5px 6px', textAlign: 'center', color: v > 0 ? '#A32D2D' : '#ccc', fontWeight: v > 0 ? 700 : 400 }}>{v || '·'}</td>;
                        })}
                        <td style={{ padding: '5px 6px', fontWeight: 800, textAlign: 'right' }}>{r.total_points}</td>
                        <td style={{ padding: '5px 6px' }}><span style={{ padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 800, background: (GRADE_COLOR[r.grade] || '#888') + '22', color: GRADE_COLOR[r.grade] || '#888' }}>{r.grade}</span></td>
                        <td style={{ padding: '5px 6px', color: '#666' }}>{r.remarks || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
                <div><div style={secStyle}>Shrinkage</div>{kv('Length wise %', rep.shrinkage?.length_pct)}{kv('Width wise %', rep.shrinkage?.width_pct)}{kv('Torque', rep.shrinkage?.torque)}</div>
                <div><div style={secStyle}>Test Reports</div>{kv('PH Value', rep.tests?.ph_value)}{kv('Dry Rubbing', rep.tests?.dry_rubbing)}{kv('Wet Rubbing', rep.tests?.wet_rubbing)}{kv('Colour Fastening', rep.tests?.colour_fastening)}{kv('Bonding Strength', rep.tests?.bonding_strength)}</div>
                <div><div style={secStyle}>Other</div>{kv('Special Finishing', rep.special_finishing)}{kv('Swatch Attached', rep.swatch_attached)}{kv('Incharge Remarks', rep.fabric_incharge_remarks)}</div>
              </div>

              <div style={secStyle}>Signatures</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
                {kv('Checker', rep.signatures?.checker)}{kv('Quality Executive', rep.signatures?.quality_executive)}{kv('Fabric Incharge', rep.signatures?.fabric_incharge)}{kv('Merchant (Deroi)', rep.signatures?.merchant)}
              </div>

              {/* ── RSR Q&Q CHECK (before GRN) ─────────────────────────────── */}
              {view.status === 'pending' && canReview && (() => {
                const docsDone = QQ_DOCS.every(([k]) => qq.docs[k] === true);
                const n = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : null; };
                const gsmReq = n(hdr.req_gsm), gsmAct = n(qq.gsm_actual);
                const gsmPass = gsmReq && gsmAct !== null ? Math.abs(gsmAct - gsmReq) / gsmReq <= 0.05 : null;
                const widReq = n(hdr.req_width), widAct = n(qq.width_actual);
                const widPass = widReq !== null && widAct !== null ? Math.abs(widAct - widReq) <= 1 : null;
                const poQty = n(view.po_qty) || 0, challanQty = n(hdr.challan_qty_kg), weigh = n(qq.weighment);
                const shortage = weigh !== null ? +(poQty - weigh).toFixed(3) : null;
                const rollsExp = n(view.total_rolls), rollsRec = n(qq.rolls_received);
                const rollsDiff = rollsExp !== null && rollsRec !== null ? rollsExp - rollsRec : null;
                const qqDone = docsDone && qq.shade && qq.overall && weigh !== null && rollsRec !== null;
                // Accept needs a price as well as a complete Q&Q — the backend refuses without
                // one, so the button says so rather than letting the click fail.
                const priced = rate !== '' && Number(rate) > 0;
                const canAccept = qqDone && priced;
                const passTag = (p) => p === null ? null
                  : <span style={{ padding: '2px 10px', borderRadius: 8, fontSize: 11, fontWeight: 800, background: p ? '#EAF7EE' : '#FCEBEB', color: p ? '#166534' : '#A32D2D' }}>{p ? 'PASS' : 'FAIL'}</span>;
                const qqIn = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
                const qqLbl = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
                const buildPayload = () => ({
                  documents: QQ_DOCS.reduce((o, [k]) => ({ ...o, [k]: qq.docs[k] === true }), {}),
                  quality: {
                    gsm_required: gsmReq, gsm_actual: gsmAct, gsm_pass: gsmPass,
                    width_required: widReq, width_actual: widAct, width_pass: widPass,
                    shade_approval: qq.shade, overall_result: qq.overall,
                  },
                  quantity: {
                    // delivered_qty is in the PO's OWN unit — kilograms for knit, metres for
                    // woven. The siblings keep their kg names for now (their own pass).
                    po_qty_kg: poQty, challan_qty_kg: challanQty, delivered_qty: weigh,
                    shortage_excess_kg: shortage, rolls_expected: rollsExp, rolls_received: rollsRec, rolls_diff: rollsDiff,
                  },
                });
                // ── Toothed gate (mirror of backend) ─────────────────────────
                // The worse of the declared and computed grade — mirrors the backend gate.
                const grade = effectiveGrade(rep);
                const gradeOverridden = rep.final_grade && rep.computed_final_grade
                  && rep.final_grade !== rep.computed_final_grade;
                const rejectOnly = grade === 'D';                          // D → reject only, no deviation
                // The screen re-implemented the backend's kilogram-only rule, so on a woven PO
                // it silently rendered no quantity warning at all. Both sides are in the PO's
                // unit now, so it verifies whenever there is an ordered quantity to compare.
                const qtyVerifiable = poQty > 0;
                const qtyPct = qtyVerifiable && weigh !== null ? (weigh - poQty) / poQty * 100 : 0;
                const qtyOut = qtyVerifiable && weigh !== null && (qtyPct < -3 || qtyPct > 5);
                const deviationBlock = !rejectOnly && (grade === 'C' || qtyOut);   // C or out-of-tol → deviation
                const blockReasons = [grade === 'C' ? 'Grade C fabric' : null, grade === 'D' ? 'Grade D fabric' : null,
                  qtyOut ? `Qty ${qtyPct > 0 ? '+' : ''}${qtyPct.toFixed(1)}% (outside −3%/+5%)` : null].filter(Boolean).join(' · ');
                return (
                  <div style={{ marginTop: 20, border: '2px solid #3B3A8F', borderRadius: 10, padding: '14px 18px', background: '#FBFBFE' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#3B3A8F', marginBottom: 10 }}>🛡 RSR Q&Q CHECK — required before GRN</div>

                    <div style={secStyle}>Step 1 · Document Verification (all must be ✅)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                      {QQ_DOCS.map(([k, label]) => {
                        const v = qq.docs[k];
                        return (
                          <button key={k} type="button" onClick={() => setQQ({ ...qq, docs: { ...qq.docs, [k]: v !== true } })}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, textAlign: 'left',
                              border: v === true ? '1px solid #166534' : '1px solid #ddd',
                              background: v === true ? '#EAF7EE' : 'white', color: v === true ? '#166534' : '#666', fontWeight: 600 }}>
                            <span style={{ fontSize: 14 }}>{v === true ? '✅' : '❌'}</span> {label}
                          </button>
                        );
                      })}
                    </div>
                    {!docsDone && <div style={{ marginTop: 8, fontSize: 12, color: '#A32D2D', fontWeight: 600 }}>⚠ Cannot proceed to Q&Q check — all 6 documents must be received. (You may still Reject.)</div>}

                    {docsDone && <>
                      <div style={secStyle}>Step 2 · Quality Check (RSR's own checks)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                        <div><label style={qqLbl}>Req GSM (from report)</label><input style={{ ...qqIn, background: '#F7F6F3' }} value={hdr.req_gsm || '—'} readOnly /></div>
                        <div><label style={qqLbl}>RSR GSM Actual</label>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input style={qqIn} type="number" step="0.1" value={qq.gsm_actual} onChange={e => setQQ({ ...qq, gsm_actual: e.target.value })} />{passTag(gsmPass)}
                          </div><div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>pass within ±5%</div></div>
                        <div><label style={qqLbl}>Req Width (from report)</label><input style={{ ...qqIn, background: '#F7F6F3' }} value={hdr.req_width || '—'} readOnly /></div>
                        <div><label style={qqLbl}>RSR Width Actual (″)</label>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input style={qqIn} type="number" step="0.1" value={qq.width_actual} onChange={e => setQQ({ ...qq, width_actual: e.target.value })} />{passTag(widPass)}
                          </div><div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>pass within ±1 inch</div></div>
                        <div><label style={qqLbl}>Shade Approval *</label>
                          <select style={qqIn} value={qq.shade} onChange={e => setQQ({ ...qq, shade: e.target.value })}>
                            <option value="">— select —</option><option value="pass">Pass</option><option value="fail">Fail</option>
                          </select></div>
                        <div><label style={qqLbl}>Overall Quality Result *</label>
                          <select style={qqIn} value={qq.overall} onChange={e => setQQ({ ...qq, overall: e.target.value })}>
                            <option value="">— select —</option><option value="accept">Accept</option><option value="conditional">Conditional</option><option value="reject">Reject</option>
                          </select></div>
                      </div>

                      <div style={secStyle}>Step 3 · Quantity Check</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                        <div><label style={qqLbl}>PO Qty (KG)</label><input style={{ ...qqIn, background: '#F7F6F3' }} value={poQty.toLocaleString('en-IN')} readOnly /></div>
                        <div><label style={qqLbl}>Challan Qty (KG)</label><input style={{ ...qqIn, background: '#F7F6F3' }} value={challanQty ?? '—'} readOnly /></div>
                        <div><label style={qqLbl}>Delivered qty measured at RSR ({uomWord(view.po_unit)}) *</label><input style={qqIn} type="number" step="0.001" value={qq.weighment} onChange={e => setQQ({ ...qq, weighment: e.target.value })} />
                          <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>Measured in the unit this PO was placed in — ordered {Number(poQty || 0).toLocaleString('en-IN')} {uomWord(view.po_unit)}.</div></div>
                        <div><label style={qqLbl}>Shortage / Excess</label>
                          <div style={{ padding: '7px 9px', fontSize: 13, fontWeight: 800, color: shortage === null ? '#aaa' : shortage > 0 ? '#A32D2D' : shortage < 0 ? '#0891b2' : '#166534' }}>
                            {shortage === null ? '—' : shortage > 0 ? `${shortage} KG short` : shortage < 0 ? `${Math.abs(shortage)} KG excess` : 'Exact ✓'}</div></div>
                        <div><label style={qqLbl}>Rolls Expected</label><input style={{ ...qqIn, background: '#F7F6F3' }} value={rollsExp ?? '—'} readOnly /></div>
                        <div><label style={qqLbl}>Rolls Received *</label><input style={qqIn} type="number" min="0" value={qq.rolls_received} onChange={e => setQQ({ ...qq, rolls_received: e.target.value })} /></div>
                        <div style={{ gridColumn: 'span 2' }}><label style={qqLbl}>Roll Difference</label>
                          <div style={{ padding: '7px 9px', fontSize: 13, fontWeight: 800, color: rollsDiff === null ? '#aaa' : rollsDiff === 0 ? '#166534' : '#A32D2D' }}>
                            {rollsDiff === null ? '—' : rollsDiff === 0 ? 'All rolls received ✓' : rollsDiff > 0 ? `${rollsDiff} roll(s) missing` : `${Math.abs(rollsDiff)} extra roll(s)`}</div></div>
                      </div>
                    </>}

                    <div style={secStyle}>Step 4 · Final Decision</div>
                    {rejectOnly && (
                      <div style={{ background: '#FCEBEB', border: '1px solid #F1B0B0', color: '#A32D2D', padding: '10px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 700 }}>
                        ⛔ Grade D fabric is <u>reject-only</u> — it cannot be accepted and has no deviation path. The only outcome is Reject (send back to Deroi).
                      </div>
                    )}
                    {deviationBlock && (
                      <div style={{ background: '#FEF3E2', border: '1px solid #F5C77E', color: '#B45309', padding: '10px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 700 }}>
                        🛡 Blocked by Q&Q gate — {blockReasons}. Accept is disabled; to proceed you must <u>Request Deviation</u> (a written reason + a second-person management approval).
                      </div>
                    )}
                    <PricePanel view={view} rate={rate} setRate={setRate} costRef={costRef} />
                    <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder={deviationBlock ? 'Deviation reason (required)…' : 'RSR remarks (required for Reject / Accept with Condition)…'}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, height: 50, resize: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button disabled={busy} onClick={() => review('reject', buildPayload())} style={{ padding: '9px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>❌ Reject — Notify Deroi</button>
                      {deviationBlock ? (
                        /* Priced here, like Accept beside it. The rate is taken from whoever
                           holds the delivery, so the two-person sign-off that follows is a
                           QUALITY decision and nothing else. */
                        <button disabled={busy || !qqDone || !remarks || !priced}
                          title={!qqDone ? 'Complete the Q&Q fields first' : (!remarks ? 'A deviation reason is required' : (!priced ? 'Enter the transfer price RSR will be billed' : ''))}
                          onClick={() => review('request_deviation', buildPayload())}
                          style={{ padding: '9px 22px', background: (qqDone && remarks && priced) ? '#B45309' : '#e5e7eb', color: (qqDone && remarks && priced) ? 'white' : '#aaa', border: 'none', borderRadius: 6, fontSize: 13, cursor: (qqDone && remarks && priced) ? 'pointer' : 'not-allowed', fontWeight: 700 }}>🛡 Request Deviation</button>
                      ) : !rejectOnly && (<>
                        <button disabled={busy || !canAccept} title={!qqDone ? 'Complete the document checklist and Q&Q fields first' : (!priced ? 'Enter the transfer price RSR will be billed' : '')} onClick={() => review('accept_with_condition', buildPayload())}
                          style={{ padding: '9px 18px', background: canAccept ? '#9A6B00' : '#e5e7eb', color: canAccept ? 'white' : '#aaa', border: 'none', borderRadius: 6, fontSize: 13, cursor: canAccept ? 'pointer' : 'not-allowed', fontWeight: 700 }}>⚠ Accept with Condition</button>
                        <button disabled={busy || !canAccept} title={!qqDone ? 'Complete the document checklist and Q&Q fields first' : (!priced ? 'Enter the transfer price RSR will be billed' : '')} onClick={() => review('accept', buildPayload())}
                          style={{ padding: '9px 22px', background: canAccept ? '#166534' : '#e5e7eb', color: canAccept ? 'white' : '#aaa', border: 'none', borderRadius: 6, fontSize: 13, cursor: canAccept ? 'pointer' : 'not-allowed', fontWeight: 700 }}>✅ Accept — Generate GRN-F</button>
                      </>)}
                    </div>
                  </div>
                );
              })()}

              {/* ── DEVIATION — second-person (management) sign-off ──────────── */}
              {view.status === 'deviation_pending' && (
                <div style={{ marginTop: 20, border: '2px solid #B45309', borderRadius: 10, padding: '14px 18px', background: '#FFFBF4' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#B45309', marginBottom: 10 }}>🛡 DEVIATION — second-person approval required</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    {kv('Blocked for', view.deviation_type)}
                    {kv('Requested by', view.deviation_requested_by_name)}
                    {kv('Final grade', gradeLabel(rep))}
                    {kv(`Delivered qty (${uomWord(view.po_unit)})`, view.qq_check_json?.quantity?.delivered_qty ?? '—')}
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #F5C77E', borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                    <span style={{ color: '#888' }}>Reason: </span><strong>{view.deviation_reason || '—'}</strong>
                  </div>
                  {!canApproveDeviation ? (
                    <div style={{ fontSize: 12, color: '#A32D2D', fontWeight: 600 }}>Only management/admin can approve a deviation.</div>
                  ) : user?.id === view.deviation_requested_by ? (
                    <div style={{ fontSize: 12, color: '#A32D2D', fontWeight: 700 }}>⚠ You requested this deviation — a different management user must sign it off (two-person rule).</div>
                  ) : (<>
                    {/* Approving a deviation IS an acceptance — it bills RSR, so it prices here too. */}
                    <PricePanel view={view} rate={rate} setRate={setRate} costRef={costRef}
                      locked={Number(view.dispatch_rate) > 0} />
                    <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Approval note (optional) / rejection reason (required to reject)…"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, height: 44, resize: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                      <button disabled={busy} onClick={() => approveDeviation(false)} style={{ padding: '9px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>❌ Reject Deviation — Send Back</button>
                      <button disabled={busy || !(rate !== '' && Number(rate) > 0)} title={rate !== '' && Number(rate) > 0 ? '' : 'Enter the transfer price RSR will be billed'}
                        onClick={() => approveDeviation(true)}
                        style={{ padding: '9px 22px', background: (rate !== '' && Number(rate) > 0) ? '#166534' : '#e5e7eb', color: (rate !== '' && Number(rate) > 0) ? 'white' : '#aaa', border: 'none', borderRadius: 6, fontSize: 13, cursor: (rate !== '' && Number(rate) > 0) ? 'pointer' : 'not-allowed', fontWeight: 700 }}>✅ Approve Deviation — Generate GRN-F</button>
                    </div>
                  </>)}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={printReport} style={printBtnStyle}>🖨 Print Report</button>
                <button onClick={() => setView(null)} style={{ padding: '9px 22px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ Defect Tracker ============================
// ─── MATERIAL FAULTS (mig 205/206) ───────────────────────────────────────────
// A defect found AFTER the GRN passed. Recording one and naming the lot it came from are
// two acts, deliberately: the first is an observation anyone on the floor can make, the
// second is a vendor claim that holds an invoice open until it is credited.
const F_LBL = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 };
const F_INP = { width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box', fontSize: 13 };
const F_STAGES = [['cutting', 'Cutting'], ['stitching', 'Stitching'], ['finishing', 'Finishing'], ['packing', 'Packing']];
const F_OUTCOMES = [['consumed', 'Consumed — already cut or stitched'],
                    ['recoverable_returned', 'Recoverable — returned to the vendor'],
                    ['recoverable_scrapped', 'Recoverable — scrapped here']];

// Shared by the escalate-from-inspection path and the standalone button. NO GRN FIELD:
// naming a lot is its own step, from the queue, under a narrower role.
function FaultModal({ m, setM, onSaved }) {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!(num(m.qty) > 0)) { setM({ ...m, err: 'Quantity must be more than zero.' }); return; }
    if (!String(m.defect_type || '').trim()) { setM({ ...m, err: 'Say what is wrong with it.' }); return; }
    setBusy(true);
    try {
      await api.post('/quality/defects', {
        stage: m.stage, gpo_no: m.gpo_no || null, qty: num(m.qty), uom: m.uom || null,
        defect_type: m.defect_type.trim(), outcome: m.outcome,
        estimated_value: m.estimated_value === '' ? null : num(m.estimated_value),
        found_at: m.found_at || null, inspection_id: m.inspection_id || null });
      onSaved();
    } catch (e) { setM({ ...m, err: e.response?.data?.error || 'Could not save' }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 22, width: 470, maxWidth: '92vw' }}>
        <div style={{ fontWeight: 700, color: '#1a1a2e' }}>⚠ Record a material fault</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4, marginBottom: 14 }}>
          A supplier problem found after the goods passed QC.
          {m.inspection_id ? ' Pre-filled from the inspection you raised it from.' : ''}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div><label style={F_LBL}>Stage</label>
            <select style={F_INP} value={m.stage} onChange={e => setM({ ...m, stage: e.target.value })}>
              {F_STAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <div><label style={F_LBL}>Found on</label>
            <input type="date" style={F_INP} value={m.found_at} onChange={e => setM({ ...m, found_at: e.target.value })} /></div>
          <div><label style={F_LBL}>GPO</label>
            <input style={F_INP} value={m.gpo_no} onChange={e => setM({ ...m, gpo_no: e.target.value })} /></div>
          <div><label style={F_LBL}>Quantity</label>
            <input type="number" step="0.001" style={F_INP} value={m.qty} onChange={e => setM({ ...m, qty: e.target.value })} /></div>
        </div>
        <div style={{ marginBottom: 10 }}><label style={F_LBL}>What is wrong with it</label>
          <input style={F_INP} value={m.defect_type} placeholder="e.g. shade variation across the roll"
            onChange={e => setM({ ...m, defect_type: e.target.value })} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
          <div><label style={F_LBL}>What became of the goods</label>
            <select style={F_INP} value={m.outcome} onChange={e => setM({ ...m, outcome: e.target.value })}>
              {F_OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <div><label style={F_LBL}>Value ₹ (optional)</label>
            <input type="number" step="0.01" style={F_INP} value={m.estimated_value}
              onChange={e => setM({ ...m, estimated_value: e.target.value })} /></div>
        </div>
        <div style={{ fontSize: 11, color: '#888', background: '#FAFAF8', border: '1px solid #eee', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
          This records that the fault happened. Saying which lot it came from is a separate
          step, from the Material Faults tab — it makes the fault claimable against a vendor.
        </div>
        {m.err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{m.err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={() => setM(null)} style={{ padding: '8px 16px', background: '#eee', color: '#555', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button disabled={busy} onClick={save} style={{ padding: '8px 16px', background: '#B45309', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{busy ? '…' : 'Record'}</button>
        </div>
      </div>
    </div>
  );
}

function MaterialFaults({ user }) {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);      // standalone record
  const [lot, setLot] = useState(null);          // name-the-lot
  const [grns, setGrns] = useState([]);
  const [busy, setBusy] = useState(false);

  const canRecord = CAN_RECORD_FAULT.includes(user?.role);
  const canAssert = CAN_ASSERT_LOT.includes(user?.role);

  const load = useCallback(() => {
    api.get('/quality/defects/pending').then(r => setRows(r.data.data || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  // The receipts to choose from. /grn-register is the real endpoint — grn-list does not
  // exist, and a picker pointing at a 404 would simply have shown an empty dropdown with
  // no error, which is the worst way for this to fail.
  useEffect(() => {
    if (!lot || grns.length) return;
    api.get('/procurement/grn-register').then(r => setGrns(r.data.data || [])).catch(() => setGrns([]));
  }, [lot, grns.length]);

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const age = (d) => d == null ? '#aaa' : d >= 14 ? '#A32D2D' : d >= 7 ? '#B45309' : '#555';

  const attributable = rows.filter(r => r.status === 'attributable');
  const unattributed = rows.filter(r => r.status === 'unattributed');

  const saveLot = async () => {
    if (!lot.grn_id) { setLot({ ...lot, err: 'Pick the GRN you believe this came from.' }); return; }
    if (!String(lot.note || '').trim()) { setLot({ ...lot, err: 'A note is required — why do you believe this?' }); return; }
    setBusy(true);
    try { await api.post(`/quality/defects/${lot.row.defect_id}/assert-lot`, { grn_id: lot.grn_id, note: lot.note.trim() });
          setLot(null); load(); }
    catch (e) { setLot({ ...lot, err: e.response?.data?.error || 'Could not save' }); }
    finally { setBusy(false); }
  };

  const section = (title, list, colour, note, showAssert) => (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: colour, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{title} ({list.length})</div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>{note}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Fault', 'Found', 'Stage', 'GPO', 'Qty', 'Vendor', 'GRN', 'Value ₹', 'Days', ''].map(h => (
          <th key={h} style={{ ...th, textAlign: ['Qty', 'Value ₹', 'Days'].includes(h) ? 'right' : 'left' }}>{h}</th>))}</tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#aaa' }}>Nothing here.</td></tr>}
          {list.map(r => (
            <tr key={r.defect_id}>
              <td style={{ ...td, fontFamily: 'monospace', color: '#B45309' }}>{r.defect_no}</td>
              <td style={{ ...td, color: '#888' }}>{r.found_at ? new Date(r.found_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</td>
              <td style={td}>{r.stage}</td>
              <td style={{ ...td, fontFamily: 'monospace' }}>{r.gpo_no || '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>{num(r.qty).toLocaleString('en-IN')} {r.uom || ''}</td>
              <td style={td}>{r.supplier || <span style={{ color: '#bbb' }}>not named</span>}</td>
              <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.grn_no || '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>{r.estimated_value != null ? num(r.estimated_value).toLocaleString('en-IN') : '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: age(r.days_open) }}>{r.days_open}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {showAssert && canAssert && (
                  <button onClick={() => setLot({ row: r, grn_id: '', note: '', err: '' })}
                    style={{ padding: '4px 10px', fontSize: 11, background: '#EEF0FA', color: '#3B3A8F', border: '1px solid #dfe3f5', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>🔗 Name the lot</button>)}
                {showAssert && !canAssert && <span style={{ fontSize: 10, color: '#bbb' }}>needs quality in-charge</span>}
              </td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: '#888' }}>
          Faults found after the goods passed QC. Recording one is an observation; naming the
          lot it came from is a claim against a vendor.
        </div>
        {canRecord && (
          <button onClick={() => setModal({ stage: 'cutting', gpo_no: '', qty: '', uom: 'PCS', defect_type: '', outcome: 'consumed', estimated_value: '', found_at: '', err: '' })}
            style={{ padding: '8px 14px', background: '#B45309', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>+ Record a fault</button>)}
      </div>

      {section('Attributable — claimable against a vendor', attributable, '#A32D2D',
        'A lot has been named. These hold their invoice open until finance raises a credit note.', false)}
      {section('Unattributed — no lot named', unattributed, '#B45309',
        'Nobody has said which lot these came from, so they cannot be credited. They are listed anyway.', true)}

      {modal && <FaultModal m={modal} setM={setModal} onSaved={() => { setModal(null); load(); }} />}

      {lot && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 22, width: 480, maxWidth: '92vw' }}>
            <div style={{ fontWeight: 700, color: '#1a1a2e' }}>🔗 Name the lot this came from</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 4, marginBottom: 12 }}>
              {lot.row.defect_no} · {lot.row.stage} · {num(lot.row.qty).toLocaleString('en-IN')} {lot.row.uom || ''} · {lot.row.defect_type}
            </div>
            {/* The wording carries what the schema enforces. No 'derived' option exists —
                not greyed, absent — because the material chain that would support one has
                no rows, and a disabled control teaches people it is coming. */}
            <div style={{ background: '#FFF8E6', border: '1px solid #f0dcb0', color: '#8A5A00', borderRadius: 6, padding: '9px 11px', fontSize: 12, marginBottom: 12 }}>
              This is an <strong>assertion</strong>. The system cannot verify it — no record links a
              garment back to a receipt. It is stored with your name and your reason.
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={F_LBL}>GRN</label>
              <select style={F_INP} value={lot.grn_id} onChange={e => setLot({ ...lot, grn_id: e.target.value })}>
                <option value="">{grns.length ? '— pick the receipt you believe this came from —' : 'loading receipts…'}</option>
                {grns.map(g => <option key={g.id} value={g.id}>{g.grn_no}{g.po_no ? ` · ${g.po_no}` : ''}{g.supplier_name ? ` · ${g.supplier_name}` : ''}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={F_LBL}>Why do you believe this?</label>
              <input style={F_INP} value={lot.note} placeholder="e.g. same lot as the July delivery, per the cutting supervisor"
                onChange={e => setLot({ ...lot, note: e.target.value })} />
            </div>
            <div style={{ fontSize: 11, color: '#A32D2D', background: '#FCEBEB', border: '1px solid #f0cccc', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
              Naming a lot makes this claimable against the vendor and <strong>will hold that
              invoice open</strong> until finance raises a credit note against it.
            </div>
            {lot.err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{lot.err}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setLot(null)} style={{ padding: '8px 16px', background: '#eee', color: '#555', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button disabled={busy} onClick={saveLot} style={{ padding: '8px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{busy ? '…' : 'Name the lot'}</button>
            </div>
          </div>
        </div>)}
    </div>
  );
}

function DefectTracker({ rows }) {
  // Aggregate defects by type and by GPO across all inspection records.
  const withDefects = rows.filter(r => num(r.defects_found) > 0);
  const totalChecked = rows.reduce((s, r) => s + num(r.qty_checked), 0);
  const totalDefects = rows.reduce((s, r) => s + num(r.defects_found), 0);

  const byType = {};
  withDefects.forEach(r => {
    const k = (r.defect_type || 'Unspecified').trim();
    byType[k] = (byType[k] || 0) + num(r.defects_found);
  });
  const typeList = Object.entries(byType).sort((a, b) => b[1] - a[1]);
  const maxType = typeList.length ? typeList[0][1] : 1;

  const byGpo = {};
  withDefects.forEach(r => {
    const k = r.gpo_no || '—';
    if (!byGpo[k]) byGpo[k] = { defects: 0, checked: 0 };
    byGpo[k].defects += num(r.defects_found);
  });
  rows.forEach(r => { if (byGpo[r.gpo_no]) byGpo[r.gpo_no].checked += num(r.qty_checked); });
  const gpoList = Object.entries(byGpo).sort((a, b) => b[1].defects - a[1].defects);

  const card = { background: 'white', borderRadius: 8, padding: '12px 16px', boxShadow: 'none', border: '1px solid #E8E7E7' };

  return (
    <div>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#999' }}>Defect log aggregated across all inspection types — Pareto of defect types and a per-GPO defect summary.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20, maxWidth: 600 }}>
        <div style={card}><div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Total Checked</div><div style={{ fontSize: 24, fontWeight: 700, color: '#0891b2' }}>{totalChecked.toLocaleString('en-IN')}</div></div>
        <div style={card}><div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Total Defects</div><div style={{ fontSize: 24, fontWeight: 700, color: '#854F0B' }}>{totalDefects.toLocaleString('en-IN')}</div></div>
        <div style={card}><div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>Overall DHU %</div><div style={{ fontSize: 24, fontWeight: 700, color: pctColor(totalChecked ? totalDefects / totalChecked * 100 : 0) }}>{totalChecked ? (totalDefects / totalChecked * 100).toFixed(2) : 0}%</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 24 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Defects by Type (Pareto)</div>
          {typeList.length === 0 ? <div style={{ color: '#aaa', fontSize: 13 }}>No defects recorded yet.</div>
            : typeList.map(([type, count]) => (
              <div key={type} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                  <span style={{ color: '#444', fontWeight: 600 }}>{type}</span>
                  <span style={{ color: '#888' }}>{count} ({(count / totalDefects * 100).toFixed(0)}%)</span>
                </div>
                <div style={{ background: '#f0f2f8', borderRadius: 4, height: 14, overflow: 'hidden' }}>
                  <div style={{ width: (count / maxType * 100) + '%', height: '100%', background: '#3B3A8F', borderRadius: 4 }} />
                </div>
              </div>
            ))}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Defects by GPO</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: '#F7F6F3' }}>
              {['GPO', 'Checked', 'Defects', 'DHU %'].map(h => <th key={h} style={{ textAlign: h === 'GPO' ? 'left' : 'right', padding: '7px 10px', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {gpoList.length === 0 ? <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: '#aaa' }}>No defects recorded.</td></tr>
                : gpoList.map(([gpo, d]) => {
                  const pct = d.checked ? d.defects / d.checked * 100 : 0;
                  return (
                    <tr key={gpo} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{gpo}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right' }}>{d.checked.toLocaleString('en-IN')}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: '#E22D2D' }}>{d.defects}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: pctColor(pct) }}>{pct.toFixed(2)}%</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================ Store Receipt QC ===============================
// QA passes/fails STORE-booked trims GRNs (GRN-R-*). rejected_qty becomes the vendor
// deduction (GRC). Deroi fabric (GRN-F-*) stays in Raw Material Inward.
function StoreReceiptQC({ user }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const canAct = ['admin', 'management', 'quality_incharge', 'qc_inspector'].includes(user?.role);
  const load = useCallback(() => {
    setErr('');
    api.get('/quality/store-grns').then(r => setRows(r.data.data || [])).catch(e => setErr(e.response?.data?.error || 'Failed to load'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (g, decision) => {
    let rejected_qty = 0, remarks = '';
    if (decision === 'fail') {
      const v = window.prompt(`Reject how many of ${g.received_qty} received? (this qty × ₹${g.rate} is deducted from the vendor)`, '');
      if (v === null) return;
      rejected_qty = Number(v);
      if (!(rejected_qty > 0) || rejected_qty > Number(g.received_qty)) { setErr(`rejected_qty must be between 1 and ${g.received_qty}`); return; }
      remarks = window.prompt('Reason (optional):', '') || '';
    } else {
      if (!window.confirm(`Pass ${g.grn_no} — accept all ${g.received_qty} of "${g.item}"?`)) return;
    }
    setBusy(g.id); setErr('');
    try { await api.patch(`/quality/store-grns/${g.id}/qc`, { decision, rejected_qty, remarks }); load(); }
    catch (e) { setErr(`${g.grn_no}: ${e.response?.data?.error || 'Action failed'}`); }
    finally { setBusy(''); }
  };

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  return (
    <div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#999' }}>Store-booked trims receipts awaiting QC. <strong>Pass</strong> accepts the full received qty; <strong>Fail</strong> records a rejected qty — <strong style={{ color: '#A32D2D' }}>rejected qty × PO rate is deducted from the vendor</strong> (GRC). Passing clears the receipt for Finance certification.</p>
      {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['GRN No', 'PO No', 'GPO', 'Item', 'Received', 'Rate ₹', 'Supplier', canAct ? 'QC Action' : ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 24 }}>No store receipts awaiting QC.</td></tr>}
          {rows.map(g => (
            <tr key={g.id}>
              <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{g.grn_no}</td>
              <td style={{ ...td, fontFamily: 'monospace' }}>{g.po_no}</td>
              <td style={{ ...td, fontFamily: 'monospace', color: '#E22D2D' }}>{g.gpo_no || '—'}</td>
              <td style={td}>{g.item || '—'}</td>
              <td style={td}>{Number(g.received_qty).toLocaleString('en-IN')}</td>
              <td style={td}>{g.rate != null ? '₹' + Number(g.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—'}</td>
              <td style={td}>{g.supplier || '—'}</td>
              {canAct && <td style={{ ...td, whiteSpace: 'nowrap' }}>
                <button disabled={busy === g.id} onClick={() => act(g, 'pass')} style={{ padding: '5px 12px', background: '#EAF7EE', border: 'none', borderRadius: 5, color: '#166534', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginRight: 5 }}>{busy === g.id ? '…' : '✓ Pass'}</button>
                <button disabled={busy === g.id} onClick={() => act(g, 'fail')} style={{ padding: '5px 12px', background: '#FCEBEB', border: 'none', borderRadius: 5, color: '#A32D2D', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✕ Fail (reject qty)</button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
